import { io, Socket } from "socket.io-client";
import { GameState, PlayCardRequest } from "../types/game";
import { sessionService } from "../services/session.service";
import { authService } from "../services/auth.service";

const SERVER_URL = process.env.REACT_APP_SERVER_URL || "http://localhost:2309";

class SocketClient {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  // Guard to prevent duplicate JOIN_ROOM emissions per socket lifecycle
  private joinRoomGuards: Map<string, { socketId: string; hasJoined: boolean }> = new Map();

  connect(): void {
    // If socket already exists and is connected, don't reconnect
    if (this.socket?.connected) {
      console.log("[SocketClient] Already connected:", this.socket.id);
      return;
    }

    // If socket exists but disconnected, try to reconnect
    if (this.socket && !this.socket.connected) {
      console.log("[SocketClient] Socket exists but disconnected, reconnecting...");
      // Update auth token before reconnecting
      const token = authService.getAuthToken();
      if (token) {
        this.socket.auth = { token };
      }
      this.socket.connect();
      return;
    }

    // Create new socket connection
    console.log("[SocketClient] Creating new socket connection...");
    const token = authService.getAuthToken();
    this.socket = io(SERVER_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      reconnectionDelayMax: 5000,
      auth: {
        token: token || undefined,
      },
    });

    this.socket.on("connect", () => {
      console.log("[SocketClient] Connected to server:", this.socket?.id);
      this.isConnected = true;
      // Reset join guards when socket reconnects (new socketId)
      this.joinRoomGuards.clear();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("[SocketClient] Disconnected from server:", reason);
      this.isConnected = false;
      // If disconnect was not intentional, socket.io will auto-reconnect
    });

    this.socket.on("connect_error", (error) => {
      console.error("[SocketClient] Connection error:", error);
      this.isConnected = false;
    });

    this.socket.on("reconnect", (attemptNumber) => {
      console.log("[SocketClient] Reconnected after", attemptNumber, "attempts");
      this.isConnected = true;
    });

    this.socket.on("reconnect_error", (error) => {
      console.error("[SocketClient] Reconnection error:", error);
    });
  }

  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      // Clear join guards on disconnect
      this.joinRoomGuards.clear();
    }
  }

  // Reset join guard for a specific room (useful when leaving a room)
  resetJoinGuard(roomId: string): void {
    const socketId = this.socket?.id;
    if (socketId) {
      const guardKey = `${socketId}:${roomId}`;
      this.joinRoomGuards.delete(guardKey);
    }
  }

  // Client → Server events
  createRoom(roomId: string, playerName: string): void {
    // Ensure user exists and get userId
    const user = sessionService.getOrCreateUser(playerName);
    this.socket?.emit("CREATE_ROOM", { roomId, playerName, userId: user.userId });
  }

  listRooms(): void {
    this.socket?.emit("LIST_ROOMS");
  }

  deleteRoom(roomId: string): void {
    this.socket?.emit("DELETE_ROOM", { roomId });
  }

  getRoomState(roomId: string): void {
    this.socket?.emit("GET_ROOM_STATE", { roomId });
  }

  getGameLogs(roomId: string): void {
    this.socket?.emit("GET_GAME_LOGS", { roomId });
  }

  joinRoom(roomId: string, playerName: string): void {
    const socketId = this.socket?.id;
    if (!socketId) {
      console.warn("[SocketClient] Socket not initialized, cannot join room");
      // Try to connect if not connected
      if (!this.socket) {
        this.connect();
      }
      // Wait a bit for connection, then retry
      setTimeout(() => {
        if (this.isSocketConnected() && this.socket?.id) {
          this.joinRoom(roomId, playerName);
        } else {
          console.error("[SocketClient] Still not connected after timeout");
        }
      }, 500);
      return;
    }

    if (!this.isSocketConnected()) {
      console.warn("[SocketClient] Socket not connected, cannot join room");
      // Wait a bit for connection, then retry
      setTimeout(() => {
        if (this.isSocketConnected() && this.socket?.id) {
          this.joinRoom(roomId, playerName);
        } else {
          console.error("[SocketClient] Still not connected after timeout");
        }
      }, 500);
      return;
    }

    // Check guard: prevent duplicate JOIN_ROOM for same socketId + roomId combination
    const guardKey = `${socketId}:${roomId}`;
    const guard = this.joinRoomGuards.get(guardKey);
    
    // If socketId changed, reset guard for this room
    if (guard && guard.socketId !== socketId) {
      this.joinRoomGuards.delete(guardKey);
    }
    
    // If already joined with this socketId, skip
    if (guard && guard.socketId === socketId && guard.hasJoined) {
      console.log(`[SocketClient] Already joined room ${roomId} with socket ${socketId}, skipping duplicate JOIN_ROOM`);
      return;
    }

    // Mark as joining
    this.joinRoomGuards.set(guardKey, { socketId, hasJoined: true });
    
    console.log("[SocketClient] Joining room:", roomId, "socket:", socketId);
    // Ensure user exists and get userId
    const user = sessionService.getOrCreateUser(playerName);
    if (this.socket) {
      this.socket.emit("JOIN_ROOM", { roomId, playerName, userId: user.userId });
    }
  }

  joinAsPlayer(roomId: string): void {
    this.socket?.emit("JOIN_AS_PLAYER", { roomId });
  }

  startGame(roomId: string): void {
    this.socket?.emit("START_GAME", { roomId });
  }

  playCard(roomId: string, request: PlayCardRequest): void {
    this.socket?.emit("PLAY_CARD", { roomId, request });
  }

  drawCard(roomId: string): void {
    this.socket?.emit("DRAW_CARD", { roomId });
  }

  skipDrawnCard(roomId: string): void {
    this.socket?.emit("SKIP_DRAWN_CARD", { roomId });
  }

  callUNO(roomId: string): void {
    this.socket?.emit("CALL_UNO", { roomId });
  }

  updateUserName(displayName: string): void {
    this.socket?.emit("UPDATE_USER_NAME", { displayName });
  }

  updateUserTheme(theme: string): void {
    this.socket?.emit("UPDATE_USER_THEME", { theme });
  }

  // Server → Client event listeners
  onRoomCreated(callback: (data: { roomId: string; userId?: string; playerName?: string }) => void): void {
    this.socket?.on("ROOM_CREATED", (data) => {
      // Save session when room is created
      if (data.userId && data.playerName) {
        sessionService.saveSession({
          userId: data.userId,
          playerName: data.playerName,
          displayName: data.playerName,
          createdAt: Date.now(),
        });
      }
      callback(data);
    });
  }

  onRoomJoined(callback: (data: { roomId: string; userId?: string; playerName?: string; hasGame?: boolean; isPlayer?: boolean; isSpectator?: boolean }) => void): void {
    this.socket?.on("ROOM_JOINED", (data) => {
      // Save session when room is joined
      if (data.userId && data.playerName) {
        sessionService.saveSession({
          userId: data.userId,
          playerName: data.playerName,
          displayName: data.playerName,
          createdAt: Date.now(),
        });
        
        // Update room info in session
        if (data.roomId !== undefined) {
          sessionService.updateRoomInfo(
            data.roomId,
            data.hasGame || false,
            data.isPlayer || false,
            data.isSpectator || false
          );
        }
      }
      // Guard is already set in joinRoom, so we don't need to set it here
      callback(data);
    });
  }

  onRoomUpdated(
    callback: (data: {
      roomId: string;
      players: Array<{ id: string; name: string; cardsCount: number; hasSaidUNO: boolean }>;
      spectators: Array<{ id: string; name: string }>;
      hasGame: boolean;
    }) => void
  ): void {
    this.socket?.on("ROOM_UPDATED", callback);
  }

  onGameStarted(callback: (data: { roomId: string }) => void): void {
    this.socket?.on("GAME_STARTED", callback);
  }

  onGameStateUpdated(callback: (state: GameState) => void): void {
    this.socket?.on("GAME_STATE_UPDATED", callback);
  }

  onInvalidMove(callback: (data: { error: string }) => void): void {
    this.socket?.on("INVALID_MOVE", callback);
  }

  onGameOver(
    callback: (data: { winnerId: string; winnerName?: string }) => void
  ): void {
    this.socket?.on("GAME_OVER", callback);
  }

  onError(callback: (data: { message: string }) => void): void {
    this.socket?.on("ERROR", callback);
  }

  onPlayerLeft(callback: (data: { playerId: string }) => void): void {
    this.socket?.on("PLAYER_LEFT", callback);
  }

  onGamePaused(callback: (data: { message: string }) => void): void {
    this.socket?.on("GAME_PAUSED", callback);
  }

  onUNOCalled(
    callback: (data: { playerId: string; playerName?: string }) => void
  ): void {
    this.socket?.on("UNO_CALLED", callback);
  }

  onCanPlayDrawnCard(callback: () => void): void {
    this.socket?.on("CAN_PLAY_DRAWN_CARD", callback);
  }

  onRoomsList(
    callback: (data: {
      rooms: Array<{
        id: string;
        playerCount: number;
        maxPlayers: number;
        createdAt: number;
        hasGame: boolean;
        hostName?: string;
        hostId?: string;
      }>;
    }) => void
  ): void {
    this.socket?.on("ROOMS_LIST", callback);
  }

  onNewRoomCreated(
    callback: (data: {
      room: {
        id: string;
        playerCount: number;
        maxPlayers: number;
        createdAt: number;
        hasGame: boolean;
        hostName?: string;
        hostId?: string;
      };
    }) => void
  ): void {
    this.socket?.on("NEW_ROOM_CREATED", callback);
  }

  onRoomUpdatedInList(
    callback: (data: {
      room: {
        id: string;
        playerCount: number;
        maxPlayers: number;
        createdAt: number;
        hasGame: boolean;
        hostName?: string;
        hostId?: string;
      };
    }) => void
  ): void {
    this.socket?.on("ROOM_UPDATED_IN_LIST", callback);
  }

  onRoomDeleted(callback: (data: { roomId: string }) => void): void {
    this.socket?.on("ROOM_DELETED", callback);
  }

  onRoomDeletedSuccess(callback: (data: { roomId: string }) => void): void {
    this.socket?.on("ROOM_DELETED_SUCCESS", callback);
  }

  onNavigateToGame(callback: (data: { roomId: string }) => void): void {
    this.socket?.on("NAVIGATE_TO_GAME", callback);
  }

  onGameLog(
    callback: (data: { id: string; roomId: string; message: string; timestamp: number }) => void
  ): void {
    this.socket?.on("GAME_LOG", callback);
  }

  onGameLogsLoaded(
    callback: (data: {
      roomId: string;
      logs: Array<{ id: string; message: string; timestamp: number }>;
    }) => void
  ): void {
    this.socket?.on("GAME_LOGS_LOADED", callback);
  }

  onUserNameUpdated(
    callback: (data: { userId: string; displayName: string }) => void
  ): void {
    this.socket?.on("USER_NAME_UPDATED", callback);
  }

  onSessionRoleUpdated(
    callback: (data: { role: "active" | "passive" }) => void
  ): void {
    this.socket?.on("SESSION_ROLE_UPDATED", callback);
  }

  onThemeUpdated(
    callback: (data: { userId: string; theme: string }) => void
  ): void {
    this.socket?.on("THEME_UPDATED", callback);
  }

  // Remove listeners
  off(event: string, callback?: (...args: any[]) => void): void {
    this.socket?.off(event, callback);
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }
}

export const socketClient = new SocketClient();
